<template>
  <div class="login-container">
    <div class="login-card">
      <h2 class="login-title">Welcome Back</h2>
      <p class="login-subtitle">Please enter your details to sign in</p>
      
      <form @submit.prevent="loginUser" class="login-form">
        <div v-if="error" class="error-message">
          {{ error }}
        </div>
        <div class="form-group">
          <label for="email">Email address</label>
          <input 
            type="email" 
            v-model="email" 
            id="email" 
            placeholder="Enter your email"
            required 
            class="form-input"
            tabindex="1"
          />
        </div>
        
        <div class="form-group">
          <div class="password-label">
            <label for="password">Password</label>
            <router-link to="/reset-password" class="forgot-password" tabindex="3">
              Forgot password?
            </router-link>
          </div>
          <input 
            type="password" 
            v-model="password" 
            id="password" 
            placeholder="Enter your password"
            required 
            class="form-input"
            tabindex="2"
          />
        </div>

        <button type="submit" class="login-button" :disabled="isLoading" tabindex="4">
          {{ isLoading ? 'Signing in...' : 'Sign in' }}
        </button>

        <p class="register-link">
          Don't have an account? 
          <router-link to="/register" tabindex="5">Create account</router-link>
        </p>
      </form>
      <VersionDisplay class="version-display" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import VersionDisplay from '@/components/VersionDisplay.vue';

export default {
  components: {
    VersionDisplay
  },
  data() {
    return {
      email: '',
      password: '',
      isLoading: false,
      error: '',
    };
  },
  methods: {
    async loginUser() {
      this.isLoading = true;
      try {
        // Use environment variable for API URL
        const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:5000/api';
        const apiUrl = `${baseUrl}/auth/login`;
        console.log('Using API URL:', apiUrl);
        
        const response = await axios.post(apiUrl, {
          email: this.email,
          password: this.password,
        });
        
        console.log('Login response:', {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          data: response.data
        });
        
        // No need to store tokens, they are in HTTP-only cookies
        this.$router.push('/weborders');
      } catch (error) {
        console.error('Login failed:', error);
        console.error('Error details:', {
          message: error.message,
          response: {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            headers: error.response?.headers
          },
          config: {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers,
            data: error.config?.data
          }
        });
        // Show the specific error message from the server if available
        this.error = error.response?.data?.message || 'Login failed. Please check your credentials and try again.';
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-container {
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: var(--background-alt);
}

.login-card {
  background-color: var(--background);
  padding: 2.5rem;
  border-radius: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 400px;
}

.login-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.login-subtitle {
  color: var(--text-secondary);
  margin-bottom: 2rem;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.password-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
}

.form-input {
  padding: 0.75rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  font-size: 1rem;
  width: 100%;
  transition: border-color 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.login-button {
  background-color: var(--primary-color);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  width: 100%;
}

.login-button:hover {
  background-color: var(--primary-hover);
}

.login-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.forgot-password {
  color: var(--primary-color);
  font-size: 0.875rem;
  text-decoration: none;
  font-weight: 500;
}

.forgot-password:hover {
  text-decoration: underline;
}

.register-link {
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.register-link a {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
}

.register-link a:hover {
  text-decoration: underline;
}

@media (max-width: 640px) {
  .login-card {
    padding: 1.5rem;
  }
  
  .login-title {
    font-size: 1.5rem;
  }
}

.version-display {
  margin-top: 1rem;
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.error-message {
  background-color: #fee2e2;
  color: #dc2626;
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  text-align: center;
}
</style>
